import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { NearmapJobHistoryItem, WorkStatus } from "../../../client/types.gen";
import { formatDuration } from "../icm/util";
import styles from "./CatCard.module.css";
import { useNearmapHistory } from "./data";

interface NearmapTabProps {
  reportId: string;
}

const renderStatus = (job: NearmapJobHistoryItem) => {
  const isActive =
    job.status === WorkStatus.PENDING || job.status === WorkStatus.IN_PROGRESS;
  if (isActive) {
    return <Tag value="In Progress" severity="info" />;
  }
  switch (job.status) {
    case WorkStatus.DONE:
      return <Tag value="Success" severity="success" />;
    case WorkStatus.FAILED:
      return <Tag value="Failed" severity="danger" />;
    default:
      return <Tag value={job.status} />;
  }
};

export const NearmapTab: React.FC<NearmapTabProps> = ({ reportId }) => {
  const { data, isLoading } = useNearmapHistory(reportId);

  const jobs = data?.jobs ?? [];
  const sortedJobs = [...jobs].sort(
    (a, b) =>
      new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  );

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <ProgressSpinner />
      </div>
    );
  }

  if (sortedJobs.length === 0) {
    return (
      <Message
        severity="info"
        text="No Nearmap runs yet. Click Run Nearmap to enrich addresses."
        className={styles.messageMargin}
      />
    );
  }

  return (
    <DataTable value={sortedJobs} size="small">
      <Column
        header="Started"
        body={(job: NearmapJobHistoryItem) =>
          new Date(job.started_at).toLocaleString()
        }
        style={{ minWidth: "160px" }}
      />
      <Column
        header="Status"
        body={renderStatus}
        style={{ minWidth: "140px" }}
      />
      <Column
        header="Duration"
        body={(job: NearmapJobHistoryItem) =>
          formatDuration(job.started_at, job.finished_at)
        }
        style={{ minWidth: "100px" }}
        align="right"
      />
      <Column
        header="Addresses"
        body={(job: NearmapJobHistoryItem) =>
          job.num_addresses.toLocaleString()
        }
        style={{ minWidth: "100px" }}
        align="right"
      />
      <Column
        header="Enriched"
        body={(job: NearmapJobHistoryItem) =>
          job.enriched_count.toLocaleString()
        }
        style={{ minWidth: "100px" }}
        align="right"
      />
    </DataTable>
  );
};
